import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PipelineTimelineEvent } from '@/types/domain/pipeline';
import { History, Database, Cpu, User, ArrowRight } from 'lucide-react';
import { SourceBadge } from '@/components/data-display/SourceBadge';

interface PipelineTimelineStreamProps {
  events: PipelineTimelineEvent[];
}

export const PipelineTimelineStream: React.FC<PipelineTimelineStreamProps> = ({ events }) => {
  const getSystemBadge = (source: PipelineTimelineEvent['systemSource']) => {
    switch (source) {
      case 'SAP':
        return <SourceBadge source="SAP" showIcon={false} className="text-[9px]" />;
      case 'Workflow':
        return (
          <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20">
            Internal Workflow
          </span>
        );
      case 'User':
        return (
          <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
            User Action
          </span>
        );
      default:
        return <SourceBadge source="PROCUREAI" showIcon={false} className="text-[9px]" />;
    }
  };

  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold text-foreground flex items-center gap-2">
            <History className="h-4 w-4 text-primary" />
            Multi-System Chronological Audit Stream ({events.length})
          </CardTitle>
          <span className="text-xs text-foreground-muted font-medium">Traceable Audit History</span>
        </div>
      </CardHeader>

      <CardContent className="pt-5">
        <div className="relative pl-6 space-y-5 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-border/60">
          {events.map((evt) => (
            <div key={evt.id} className="relative group">
              <div className="absolute -left-6 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-card ring-4 ring-card border border-border">
                {evt.systemSource === 'SAP' ? (
                  <Database className="h-3 w-3 text-blue-600" />
                ) : evt.systemSource === 'Workflow' ? (
                  <Cpu className="h-3 w-3 text-purple-600" />
                ) : (
                  <User className="h-3 w-3 text-primary" />
                )}
              </div>

              <div className="rounded-xl border border-border/80 p-3 bg-card/60 hover:bg-card transition-all space-y-1 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-foreground">{evt.title}</span>
                    {getSystemBadge(evt.systemSource)}
                  </div>

                  <span className="font-mono text-[10px] text-foreground-muted">
                    {new Date(evt.timestamp).toLocaleString()}
                  </span>
                </div>

                <p className="text-foreground-secondary text-[11px] leading-relaxed">{evt.description}</p>

                <div className="flex items-center justify-between pt-1 border-t border-border/40 text-[10px] text-foreground-muted">
                  <span>Actor: <strong className="text-foreground">{evt.actorName}</strong></span>
                  {evt.documentRef && (
                    <span className="font-mono font-bold text-primary flex items-center gap-1">
                      Ref: {evt.documentRef} <ArrowRight className="h-2.5 w-2.5" />
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
